import { WeaponCharge } from "../effects/weapon_charge"
import { ItemTypeMeta, locateInventoryActiveItem, entityHeldObjectSpriteId, locateInventoryItemObjectByItemType } from "game-common/item/item"
import { LpcSheet, toDescriptor } from "game-common/lpc/lpc"
import { attackActionFor, defaultActionFor, forgeLpcSpritesheetDescriptors, getLpcBodyShadow, getLpcBodyPlacement, LpcRaceDimensions } from "game-common/lpc/lpc-forge"
import { InteractionType } from "game-common/mechanics/entity_mechanics"
import { Mechanics } from "game-common/mechanics/mechanics"
import { behaviorRaceFor } from "game-common/character/sprite_definition"
import { AnimatedFeedbackParams, EmberRosterRole, Entity, EntityId, TileSize, getCurrentTime } from "game-common/models"
import { StatusHaloTier } from "./member_status_halo"
import { Container, Graphics, filters } from "pixi.js"
import { logger } from "../../../../logger"

import { EntityInteractionCallback, PlayerEntityId } from "../../../../client_game_logic"
import { AnimatedFeedback } from "../effect/animated_feedback"
import { WorldOverlayItem } from "../world_overlay_surface"
import { AvatarHud } from "./avatar_hud"
import { BaseRenderable, SpeechOptions } from "./base_renderable"
import { HeldPose, LoadObserver, LpcCompositeSprite } from "./lpc_composite_sprite"
import { Callback } from "game-common/util"
import { Fader } from "../gui/fader"
import { ItemTypes } from "game-common/item/item_type"
import { getLpcBodyGroundAnchor } from "game-common/lpc/lpc-forge"
import { BODY_GROUND_OFFSET, BODY_RENDER_LIFT, resolveBodyGroundAnchor } from "game-common/lpc/body_placement"

export class LpcComposite extends BaseRenderable {
    private groundAnchor = { x: 32, y: 60 }
    private bodyRace = ""
    private spriteScale = 1
    private sprite: LpcCompositeSprite
    private hud: AvatarHud
    private drawableSurface: BaseRenderable
    private clickArea: BaseRenderable
    private worldOverlayItem: WorldOverlayItem
    private halo: boolean
    private haloObject: Graphics
    // Another player's body, not this client's and not an NPC: it gets the ground ring that marks a
    // real person (see player_ring). Held here rather than on the sprite because the sprite is thrown
    // away and rebuilt on every appearance change.
    private remotePlayer: boolean
    private entityId: EntityId
    blinkingInterval: any
    private rebuildTimeout: any
    private animatedFeedback: AnimatedFeedback
    private loadObserver: LoadObserver
    private colorMatrix: any
    private damageFlashTimeout: any
    private submerged: boolean
    // The pose this body is currently held in, mirrored here (not read off the sprite) because the
    // sprite is thrown away and rebuilt on every appearance change and the pose has to outlive it.
    private heldPose?: HeldPose
    // False until this body has been through one doMovement. It separates "we watched them fall",
    // which plays the collapse, from "they were already down when we first drew them", which snaps
    // straight to the prone frame -- a body that replayed its fall every time it came back into
    // view would look like it kept falling over.
    private poseSeen = false
    private weaponChargeIndicator?: WeaponCharge

    constructor(
        entityId: EntityId,
        loadObserver?: LoadObserver,
        isClientPlayer?: boolean,
        isRemotePlayer?: boolean,
    ) {
        super()
        this.entityId = entityId
        this.remotePlayer = isRemotePlayer === true
        this.clickArea = new BaseRenderable()
        this.loadObserver = loadObserver
        this.hud = new AvatarHud(
            0xffffff,
            {
                renderActions: {},
                alwaysShowLabel: false,
            },
            this.clickArea,
            isClientPlayer,
        )

        this.drawableSurface = new BaseRenderable()
        this.animatedFeedback = new AnimatedFeedback()

        // The ColorMatrixFilter is only needed for the brief damage flash. A filtered display
        // object is rendered into its own framebuffer and cannot batch with its neighbours, so
        // leaving it attached costs an offscreen render pass per character every frame. Attach it
        // only while flashing (see doDamaged) and detach it afterwards.
        this.colorMatrix = new filters.ColorMatrixFilter()
    }

    getClickSurface = (): BaseRenderable => {
        return this.clickArea
    }

    private rebuildLayers = (entity: Entity) => {
        const dimensions = Mechanics.entity.body.dimensions(entity)

        const race = entity.appearance?.race
        this.bodyRace = race
        const sex = entity.appearance?.sex
        const hair = entity.appearance?.hair
        const nose = entity.appearance?.nose
        const eyes = entity.appearance?.eyes

        const weaponAction = attackActionFor(race, entity?.weapon?.weaponType)

        const descriptors = forgeLpcSpritesheetDescriptors({
            body: {
                race,
                sex,
                color: entity.appearance?.bodyColor,
            },
            hair: hair
                ? {
                      component: hair,
                      color: entity.appearance?.hairColor,
                  }
                : undefined,
            nose: nose
                ? {
                      component: nose,
                      color: entity.appearance?.noseColor,
                  }
                : undefined,
            eyes: eyes
                ? {
                      component: eyes,
                      color: entity.appearance?.eyesColor,
                  }
                : undefined,
            ///////
            weapon: toDescriptor(
                locateInventoryItemObjectByItemType(entity.inventory, entity?.weapon?.weaponType as ItemTypes)
                    ?.itemTypeMeta,
            ),
            pants: toDescriptor(locateInventoryActiveItem(entity.inventory, "pants")),
            shirt: toDescriptor(locateInventoryActiveItem(entity.inventory, "shirt")),
            armor: toDescriptor(locateInventoryActiveItem(entity.inventory, "armor")),
            shoes: toDescriptor(locateInventoryActiveItem(entity.inventory, "shoes")),
            shield: toDescriptor(locateInventoryActiveItem(entity.inventory, "shield")),
            cape: toDescriptor(locateInventoryActiveItem(entity.inventory, "cape")),
            bauldron: toDescriptor(locateInventoryActiveItem(entity.inventory, "bauldron")),
        })

        const all = []
        descriptors.body?.forEach(next => all.push(next))
        const isHumanoid = Mechanics.entity.body.isHumanoid(race)
        const undead = entity.faction === "undead" || ["skeleton", "zombie"].includes(behaviorRaceFor(race))
        const bodyShadow = getLpcBodyShadow(race)
        if (isHumanoid) {
            descriptors.hair?.forEach(next => all.push(next))
            descriptors.nose?.forEach(next => all.push(next))
            descriptors.eyes?.forEach(next => all.push(next))

            descriptors.weapon?.forEach(next => all.push(next))
            descriptors.pants?.forEach(next => all.push(next))
            descriptors.shirt?.forEach(next => all.push(next))
            descriptors.armor?.forEach(next => all.push(next))
            descriptors.shoes?.forEach(next => all.push(next))
            descriptors.shield?.forEach(next => all.push(next))
            descriptors.cape?.forEach(next => all.push(next))
            descriptors.bauldron?.forEach(next => all.push(next))
        }

        const sortedDescriptors = all.sort((a: LpcSheet, b: LpcSheet) => a.zIndex - b.zIndex)
        const heldObject = isHumanoid ? entityHeldObjectSpriteId(entity) : undefined

        const id = race + " " + entity?.safeName()
        const priority = PlayerEntityId.value === entity.entityId
        const oldSprite = this.sprite

        let loadObserver
        if (this.loadObserver) {
            loadObserver = this.loadObserver
            oldSprite?.remove()
        } else {
            if (oldSprite) {
                loadObserver = new LoadObserver(() => {
                    new Fader(oldSprite, {
                        releaseCallback: () => {
                            oldSprite?.remove()
                        },
                        fadeAlphaDecrement: 0.05,
                    }).update()
                })
            }
        }

        loadObserver?.clear()

        this.sprite = new LpcCompositeSprite({
            attackAction: weaponAction,
            angle: entity.movement.angle,
            descriptors: sortedDescriptors,
            hasHalo: this.halo,
            hasShadow: isHumanoid || bodyShadow !== undefined,
            feetY: bodyShadow?.feetY,
            bodyFrame: LpcRaceDimensions[race] || { w: 64, h: 64 },
            placement: getLpcBodyPlacement(race),
            groundAnchor: getLpcBodyGroundAnchor(race),
            // The ring says "a person is driving this". An INVADER is a person driving one of the
            // camp's own attackers, and the whole point of the body is that the defender cannot tell
            // -- so it is withheld. If the marked variant of the design wins instead, this is where a
            // hostile-coloured ring goes, and "the ring ignites on breaking out" is the concealment
            // variant's reveal (see docs/ember-the-fallen-design.md).
            hasPlayerRing: this.remotePlayer && !entity.invading,
            breathingSeed: isHumanoid && !undead ? entity.entityId : undefined,
            id,
            priority,
            loadObserver: loadObserver,
            isSubmerged: this.submerged,
            heldObject: heldObject ? {
                spriteId: heldObject,
                sex: sex || "male",
            } : undefined,
            defaultAction: defaultActionFor(entity.appearance),
            // Carried across the rebuild: a fallen body whose inventory changes (looted, or handed
            // something) must not stand up, play the fall again and lie back down.
            heldPose: this.heldPose,
        })

        this.sprite.initalize(id, entity.movement.angle)
        // The sprite above is brand new, so anything the composite is holding on this member's
        // behalf has to be put back onto it.
        this.applyStatusHalo()
        this.haloObject = this.sprite.halo
        const raceDimensions = LpcRaceDimensions[entity?.appearance?.race] || {
            w: 64,
            h: 64,
        }
        this.groundAnchor = resolveBodyGroundAnchor(raceDimensions.w, raceDimensions.h,
            { groundAnchor: getLpcBodyGroundAnchor(race), shadow: bodyShadow })
        this.updateScale(entity.spriteScale ?? this.spriteScale)
        this.addChild(this.sprite)

        if (this.clickArea.parent) {
            this.clickArea.parent.removeChild(this.parentArea)
        }
        this.clickArea.clear()
        this.clickArea.beginFill(0xff0000, 0.0001)
        this.clickArea.drawRect(0, 0, dimensions.w, dimensions.h)
        this.clickArea.x = -dimensions.w / 2
        this.clickArea.y = -dimensions.h / 2 + TileSize * 0.15
        this.clickArea.endFill()
        this.addChild(this.clickArea)

        if (this.haloObject) {
            this.updateHaloColor(entity.hp, entity.maxHp)
        }
    }

    doMovement(entity?: Entity) {
        if (entity?.weaponCharge && !this.weaponChargeIndicator) {
            this.weaponChargeIndicator = new WeaponCharge()
            // Its ground half goes UNDER the body, at index 0, so the character stands inside the
            // charge ring instead of wearing it. Rebuilding the sprite re-appends the sprite and
            // click area to the end of the child list, so index 0 keeps meaning "below everything".
            this.addChildAt(this.weaponChargeIndicator.ground, 0)
            this.addChild(this.weaponChargeIndicator)
        }
        this.weaponChargeIndicator?.draw(entity?.weaponCharge)
        this.updateHeldPose(entity)
        this.sprite?.doMovement(entity)
        if (this.sprite) {
            // The LIMP: a tilt that sways while an escorted survivor is actually walking. The other
            // half of the injury presentation, the resting pose, used to be a 0.72 vertical squash
            // of the standing sprite here; it is a real prone pose now (see updateHeldPose), so
            // there is nothing left to deform. Suppressed while the body is on the ground, where a
            // tilt would only rock a face-down sprite.
            //
            // entity.flags is wiped on every message the client receives (see
            // Client.updateEntityFromMessage), so this reads the wire field, not a flag.
            const limping = !this.heldPose && entity?.rescuePose === "following" && entity.movement.moving
            this.sprite.rotation = limping ? -0.06 + 0.04 * Math.sin(getCurrentTime() / 180) : 0
        }
        this.drawableSurface.doMovement(entity)
    }

    /**
     * Lay this body down, or stand it back up.
     *
     * TWO ROADS TO THE SAME POSE, and the renderer deliberately cannot tell them apart. A fallen
     * PLAYER is `downed`, the server's verdict on a deadline it owns. An injured ember SURVIVOR is
     * `rescuePose`, a phase of the rescue that ends when somebody finishes holding to help her up.
     * Both mean "this body is on the ground", which is the only question this method asks -- so the
     * get-up animation, the guards that stop a prone body walking or turning, and the snap-vs-fall
     * decision are all written once and serve both.
     *
     * HUMANOIDS ONLY. The pose is the LPC `hurt` row's final frame, and that row exists only on the
     * humanoid sheets -- a rabbit or a slime has no frames for it at all, so asking would bake
     * nothing, fail, and leave the creature in whatever action it was already in while this class
     * believed it was posed. Rather than rely on that failing quietly, non-humanoids are never
     * asked, and go on behaving exactly as they did before this existed.
     */
    private updateHeldPose(entity?: Entity) {
        if (!entity) {
            return
        }
        const onTheGround = !!entity.downed || entity.rescuePose === "downed"
        const downed = onTheGround && Mechanics.entity.body.isHumanoid(entity.appearance?.race)
        const pose: HeldPose | undefined = downed ? "downed" : undefined
        if (pose !== this.heldPose) {
            this.heldPose = pose
            // Play the fall only if we were watching before it happened; see poseSeen.
            this.sprite?.setHeldPose(pose, this.poseSeen)
        }
        this.poseSeen = true
    }

    update(hp: number, hpMax: number) {
        this.hud.update(hp, hpMax)
        this.updateHaloColor(hp, hpMax)
    }

    updateName(name: string) {
        this.hud.updateName(name)
    }

    recolor(color: number) {
        this.hud.recolor(color)
    }

    updateHaloColor(hp: number, hpMax) {
        if (!this.haloObject) {
            return
        }

        const hpPct = hp / hpMax
        if (hpPct < 0.7) {
            if (hpPct < 0.4) {
                this.haloObject.tint = 0xff0000
            } else {
                this.haloObject.tint = 0xffa500
            }
        } else {
            this.haloObject.tint = 0x00ff00
        }
    }

    updateBlinking(blinking: boolean): void {
        if (!blinking) {
            this.visible = true
            if (this.blinkingInterval) {
                clearInterval(this.blinkingInterval)
                this.blinkingInterval = null
            }
        } else {
            if (this.blinkingInterval) {
                return
            }

            this.blinkingInterval = setInterval(() => {
                this.visible = !this.visible
            }, 100)
        }
    }

    addTag() {
        this.hud.addTag()
    }

    removeTag() {
        this.hud.removeTag()
    }

    updateLevel(level: number) {
        this.hud.updateLevel(level)
    }

    doAttack(entity: Entity, callback?: Callback<void>) {
        this.sprite?.doAttack(entity, callback)
    }

    doDamaged(entity: Entity) {
        this.sprite?.doDamaged(entity)

        // Attach the filter only for the duration of the flash, then detach it so the character
        // batches normally again (see constructor).
        this.filters = [this.colorMatrix]
        this.colorMatrix.blackAndWhite(true)
        this.colorMatrix.contrast(1, true)
        this.colorMatrix.saturate(1, true)

        // Guard against overlapping hits resetting the flash early.
        if (this.damageFlashTimeout !== undefined) {
            clearTimeout(this.damageFlashTimeout)
        }
        this.damageFlashTimeout = setTimeout(() => {
            this.colorMatrix.reset()
            this.filters = null
            this.damageFlashTimeout = undefined
        }, 100)
    }

    // Called on entity removal (EntityRenderingManager.destroy). Cascades teardown into the inner
    // sprite -- which clears its cleanup timers and releases its generator clones -- and clears
    // this wrapper's own timers. Without this, entity removal (the common discard path, distinct
    // from a paperdoll rebuild) leaked both.
    cleanup() {
        if (this.rebuildTimeout !== undefined) {
            clearTimeout(this.rebuildTimeout)
            this.rebuildTimeout = undefined
        }
        if (this.damageFlashTimeout !== undefined) {
            clearTimeout(this.damageFlashTimeout)
            this.damageFlashTimeout = undefined
        }
        if (this.blinkingInterval) {
            clearInterval(this.blinkingInterval)
            this.blinkingInterval = null
        }
        this.sprite?.cleanup()
        this.hud?.cleanup()
    }

    spoke(message: string, options?: SpeechOptions) {
        this.hud.spoke(message, options)
    }

    releaseHeldSpeech() {
        this.hud.releaseHeldSpeech()
    }

    getDrawableSurface(): Container {
        return this.drawableSurface.getDrawableSurface()
    }

    fragment = (entity: Entity) => {
        this.sprite?.fragment(entity)
        this.drawableSurface.fragment(entity)
    }

    /**
     * Is this body drawable yet? No while its spritesheets are being fetched and baked, and no while
     * a build is merely scheduled -- both are states in which this container is on the stage with
     * nothing in it. Read by the room-reveal hold (see RoomReveal) to decide whether a freshly
     * loaded room is worth opening the screen on.
     *
     * A composite that has never been handed an appearance answers YES: there is no sprite coming,
     * so there is nothing to wait for. Entities beyond the server's detail range arrive exactly like
     * that -- a brief message with no appearance on it -- and a room full of distant wildlife must
     * not hold the curtain shut on a camp that is otherwise complete.
     */
    isReadyToShow(): boolean {
        if (!this.sprite && this.rebuildTimeout === undefined) {
            return true
        }
        return this.rebuildTimeout === undefined && !!this.sprite?.initialized
    }

    updatePaperdoll(entity: Entity): void {
        if (!entity) {
            return
        }
        const { appearance } = entity
        if (!appearance) {
            return
        }

        if (this.rebuildTimeout !== undefined) {
            clearTimeout(this.rebuildTimeout)
        }

        // console.trace()

        // The FIRST build is barely debounced, every later one is. The wait exists to coalesce
        // churn -- an appearance change arrives as several messages (the body, then the inventory
        // that dresses it) and each rebuild throws away a composited sprite and bakes another, so
        // 250ms of patience is cheap when there is already a body on screen to look at meanwhile.
        //
        // Before the first build there is nothing on screen: the wait is pure latency, and it is
        // spent BEFORE the spritesheets are even asked for. Measured on a character-creation drop
        // into a camp, that debounce was 250ms of the ~510ms between the room landing and its lord
        // being drawable -- half the pop-in the room-reveal hold now has to wait out (see
        // RoomReveal). Keep a short window so an inventory arriving a tick behind the appearance
        // still builds once rather than twice.
        const delay = this.sprite ? 250 : 60

        this.rebuildTimeout = setTimeout(() => {
            this.rebuildLayers(entity)
            this.rebuildTimeout = undefined
        }, delay)
    }

    // Possession happens to a sprite that already exists -- the player is standing there and then
    // they are wearing a skeleton -- so the ring has to be answerable after construction too, not
    // only when the layers are rebuilt from scratch.
    updatePlayerRing(entity: Entity): void {
        this.sprite?.setPlayerRingVisible(!!this.remotePlayer && !entity?.invading)
    }

    // Which ground disc this body is wearing, mirrored so it survives the sprite being rebuilt under
    // us. See updateStatusHalo.
    private statusHaloTier?: StatusHaloTier

    updateHalo(entity: Entity, enabled: boolean) {
        this.halo = enabled
        this.updatePaperdoll(entity)

        if (this.haloObject) {
            this.updateHaloColor(entity.hp, entity.maxHp)
        }
    }

    updateSpeed(factor: number) {
        //
    }

    onMouseOver = (show: boolean) => {
        this.hud.onMouseOver(show)
    }

    // Pose-accurate hover outline. The glow belongs to the sprite, which is the only thing that knows
    // which baked frame is on screen this instant and which carries the oversize offsets the outline
    // has to inherit -- and it is replaced wholesale on a paperdoll rebuild, so the highlight has to
    // live and die with it. A composite with no sprite yet (or none at all) reports false, and the
    // caller outlines whatever map tiles stand in for it.
    setHighlighted = (on: boolean): boolean => {
        if (!this.sprite) {
            return false
        }
        this.sprite.setHighlighted(on)
        return true
    }

    setWorldOverlayItem = (item: WorldOverlayItem) => {
        this.worldOverlayItem = item
        item.headAttachmentWorld = () => this.sprite?.headAttachmentWorld(this.bodyRace)
        item.heldAttackGripWorld = () => this.sprite?.heldAttackGripWorld(this.bodyRace)
        this.worldOverlayItem.addChild(this.hud)
    }

    updatePointerIcon(show: boolean, angle?: number) {
        this.hud.updatePointerIcon(show, angle)
    }

    itemPickedUp(entity: Entity, itemTypeMeta: ItemTypeMeta, quantity: number) {
        this.feedback(
            entity,
            itemTypeMeta.spriteId || `${itemTypeMeta.type}-${itemTypeMeta.color}.png`,
            quantity > 0 ? `+${quantity}` : "",
            {
                font: "EntityImportantFont",
                color: 0x00ff00,
            },
        )
    }

    // Floating feedback over this body: a pickup icon, a damage number, the "!!!" alert. A running
    // body's dust trail does NOT come through here -- it is ground art that has to z-sort against
    // other entities, which nothing parented into the overlay can do. EntityRenderingManager.feedback
    // routes `type: "foot"` to FootDust before it ever reaches a renderable.
    feedback(entity: Entity, image: string, label: string, params?: AnimatedFeedbackParams) {
        this.animatedFeedback.setup(
            0,
            this.worldOverlayItem.height / 2,
            image,
            label,
            this.worldOverlayItem,
            params,
            this.sprite,
        )
    }

    updateSigil(sigil?: string) {
        this.hud.updateSigil(sigil)
    }

    updateRole(role: EmberRosterRole | null) {
        this.hud.updateRole(role)
    }

    // The in-world ground disc. Kept on the composite as well as pushed to the sprite because the
    // sprite is REPLACED on every paperdoll rebuild, and the rebuild is the common case for exactly
    // the members this marks: going hungry swaps the member's brain, which re-stamps its appearance.
    // A disc that lived only on the sprite would therefore go out at the moment the member started
    // walking off its post, which is the moment the player needs it. Reapplied in rebuildLayers,
    // alongside heldPose, which is carried across for the same reason.
    updateStatusHalo(tier?: StatusHaloTier) {
        this.statusHaloTier = tier
        this.applyStatusHalo()
    }

    private applyStatusHalo() {
        this.sprite?.setStatusHaloTier(this.statusHaloTier)
    }

    // A fixed lift from the canonical ground reference. Reading this.height would let
    // changing animation bounds or oversize weapons raise and lower the entire body.
    getYOffset() {
        // Obstacles are authored against the human sole line, 21.6px below location.y.
        // Native frame size must not change that ground reference (or a chicken stands
        // above the roots while its ground position already sorts in front of the tree).
        return BODY_RENDER_LIFT
    }

    grassContact() {
        if (!this.sprite?.initialized || this.submerged || !this.parent) return undefined
        // The ground stays fixed while the body leans or breathes above it.
        const point = this.parent.toLocal(this.toGlobal({ x: 0, y: BODY_GROUND_OFFSET - BODY_RENDER_LIFT }))
        const angle = { front: Math.PI / 2, back: -Math.PI / 2, left: Math.PI, right: 0 }[this.sprite.facingDir]
        return { x: point.x, y: point.y, scale: this.spriteScale, angle }
    }

    updateScale(scale = 1) {
        if (!Number.isFinite(scale) || scale <= 0) return
        this.spriteScale = scale
        if (!this.sprite) return
        this.sprite.scale.set(scale)
        this.sprite.position.set(-this.groundAnchor.x * scale,
            BODY_GROUND_OFFSET - BODY_RENDER_LIFT - this.groundAnchor.y * scale)
    }

    updateSubmerged(submerged: boolean) {
        this.submerged = submerged
    }

    updateAfk(afk: boolean) {
        if (this.sprite) {
            this.sprite.alpha = afk ? 0.3 : 1.0
        }
    }

    updateActivation(entity: Entity, active: boolean) {
        if (!this.sprite) {
            return
        }

        this.sprite.activateAnimation(active ? "activate" : "deactivate")
    }
}
